import React, { useMemo, useState } from "react";
import { Plus, Search, Trash2, X } from "lucide-react";

import ButtonV2 from "../../../components/ui/button/v2";

export interface ManageModalItem {
  id: string;
  title?: string;
  subtitle?: string;
}

interface ManageEntitiesModalProps {
  title: string;
  items: ManageModalItem[];

  addButtonTitle: string;

  saveEnabled?: boolean;

  onClose: () => void;
  onAdd: () => void;
  onDiscard?: () => void;
  onSave?: () => void;
  onDelete?: (item: ManageModalItem) => void;
}

const ManageEntitiesModal: React.FC<
  ManageEntitiesModalProps
> = ({
  title,
  items,
  addButtonTitle,
  saveEnabled = true,
  onClose,
  onAdd,
  onDiscard,
  onSave,
  onDelete,
}) => {
  const [searchText, setSearchText] = useState("");

  const filteredItems = useMemo(
    () =>
      items.filter(item =>
        `${item.title ?? ""} ${item.subtitle ?? ""}`
          .toLowerCase()
          .includes(searchText.toLowerCase())
      ),
    [items, searchText]
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(10,10,10,0.20)]"
      onClick={onClose}
    >
      <div
        className="flex w-full max-w-[518px] flex-col rounded-[10px] border border-[#E5E5E5] bg-white shadow-lg"
        onClick={e => e.stopPropagation()}
      >
        <div className="relative px-4 pt-4 pb-5">
          <h2 className="text-[18px] font-semibold leading-[18px] text-[#0A0A0A]">
            {title}
          </h2>

          <button
            onClick={onClose}
            className="absolute right-4 top-4 flex h-4 w-4 items-center justify-center text-[#737373]"
          >
            <X size={16} className="relative top-[0.5px] text-[#0A0A0A] opacity-70" />
          </button>
        </div>

        <div className="flex flex-col">
          <div className="mb-4 px-4">
            <div className="flex h-9 items-center gap-2 rounded-md border border-[#E5E5E5] px-3 shadow-[0_1px_2px_0_rgba(0,0,0,0.05)]">
              <Search
                size={16}
                className="shrink-0 text-[#737373]"
              />

              <input
                type="text"
                placeholder="Search"
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                className="flex-1 bg-transparent text-sm outline-none placeholder:text-[#737373]"
              />
            </div>
          </div>

          <div className="max-h-[360px] overflow-y-auto">
            {filteredItems.length === 0 ? (
            <div className="flex items-center justify-center py-2">
              <p className="text-sm text-[#737373]">
                No results found
              </p>
            </div>
            ) : (
              filteredItems.map(item => (
                <div
                  key={item.id}
                  className="flex items-center border-b border-[#E5E5E5] px-4 py-2"
                >
                  <div className="flex min-w-0 flex-1 flex-col gap-[2px]">
                    {item.title && (
                      <p className="truncate text-sm font-normal leading-5 text-[#0A0A0A]">
                        {item.title}
                      </p>
                    )}

                    {item.subtitle && (
                      <p className="truncate text-xs font-normal leading-4 text-[#737373]">
                        {item.subtitle}
                      </p>
                    )}
                  </div>

                  {onDelete && (
                    <button
                      onClick={() => onDelete(item)}
                      className="ml-4 flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-[#737373]"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              ))
            )}
          </div>

          <div className="mt-5 px-4">
            <button
              onClick={onAdd}
              className="flex h-9 w-full items-center justify-center gap-2 rounded-[8px] bg-[#F5F5F5] px-4 py-2 text-sm font-medium leading-5 text-[#171717]"
            >
              <Plus size={16} className="shrink-0" />
              <span>{addButtonTitle}</span>
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-4 py-4">
          {onDiscard && (
            <ButtonV2
              variant="secondary"
              onClick={onDiscard}
            >
              Discard
            </ButtonV2>
          )}

          {onSave && (
            <ButtonV2
              onClick={onSave}
              disabled={!saveEnabled}
              className="h-9 rounded-md bg-[linear-gradient(180deg,#788AF7_0%,#3551F3_100%)] text-white shadow-[0px_1px_2px_rgba(0,0,0,0.05)] disabled:opacity-50"
            >
              Save Changes
            </ButtonV2>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageEntitiesModal;